import type { TypeOf, ZodTypeAny } from 'zod';
import {
  mockupSchema,
  orderSchema,
  partnerConfigSchema,
  printAreaSchema,
  printSchema,
  productSchema,
  type Mockup,
  type Order,
  type PartnerConfig,
  type Print,
  type PrintArea,
  type Product,
} from './schemas';

const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Запрос с разбором ответа по zod-схеме.
 *
 * NOTE: обобщение идёт по самой схеме, а не по типу данных. У схем с
 * `.default()` входной и выходной типы различаются — поле необязательно
 * в JSON, но всегда присутствует после разбора. `TypeOf` берёт выходной.
 */
async function request<S extends ZodTypeAny>(
  path: string,
  schema: S,
  init?: RequestInit,
): Promise<TypeOf<S>> {
  const response = await fetch(`${BASE}/api/v1${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new ApiError(detail || response.statusText, response.status);
  }

  return schema.parse(await response.json());
}

export const api = {
  partnerConfig: (slug: string): Promise<PartnerConfig> =>
    request(`/partners/${slug}/config`, partnerConfigSchema),

  products: (slug: string): Promise<Product[]> =>
    request(`/partners/${slug}/products`, productSchema.array()),

  prints: (slug: string, artist?: string): Promise<Print[]> =>
    request(
      `/partners/${slug}/prints${artist ? `?artist=${encodeURIComponent(artist)}` : ''}`,
      printSchema.array(),
    ),

  placements: (): Promise<PrintArea[]> => request('/placements', printAreaSchema.array()),

  createOrder: (slug: string, payload: unknown, idempotencyKey: string): Promise<Order> =>
    request(`/partners/${slug}/orders`, orderSchema, {
      method: 'POST',
      // Повтор с тем же ключом вернёт тот же заказ, а не создаст второй
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(payload),
    }),

  renderMockup: (payload: unknown): Promise<Mockup> =>
    request('/mockup/render', mockupSchema, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * Отправка события воронки.
   *
   * Возвращает обещание, чтобы вызывающий код видел неудачу и мог перестать
   * слать события в недоступный приёмник. Экран это не роняет: обещание
   * обрабатывается там, где вызывается.
   */
  trackEvent: (payload: unknown): Promise<void> =>
    fetch(`${BASE}/api/v1/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).then((response) => {
      if (!response.ok) throw new Error(`events ${response.status}`);
    }),
};
